import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { columnsheadId, showText } from '../system/columnshead';
import { Tooltip, Image, Button, Select, Space, Checkbox } from 'antd';
import antdMessage from '../system/antdMessage';
import dayjs from 'dayjs';
import FileUpload from './FileUpload';
import BaseWebUniversal, { DeleteOptions, BaseWebUniversalProps } from '../system/BaseWebUniversal';
import fetchApi from '../../../utils/api/fetchApi';
import getList from '../../universal/getList';
import { CommonProps, defaultRtn, RtnType } from '../../../utilsTs/declareTypes';
import { useMergedRef } from '../hooks/general';
import numberHandle from '../../../utilsTs/baseLib/numberHandle';
import FilesEdit from './FilesEdit';

/** 插入文件方法选项 */
export interface InsertFileOptions extends Pick<BaseWebUniversalProps, 'sendPageData'> {
    /** 文件数据 */
    fileRows: Array<{
        /** 文件id */
        id: string;
        dateSubdirectory: string;
        deletionDate: Date;
        destination: string;
        fileMD5: string;
        fileName: string;
        fileSize: number;
        fileType: string;
        fileValidityPeriod: any;
        fileValidityPeriodId: string;
        filesGroup: any;
        filesGroupId: string;
        imageHeight: number;
        imageWidth: number;
        isUse: boolean;
        originalFileName: string;
        /** 文件服务器地址 */
        serverUrl: string;
        smallImage: string;
        subdirectory: string;
        sysDate: any;
        userId: string;
        viewUrl: string;
    }>
};

/** 默认选择 */
interface DefaultSelectedOptions {
    /** 文件服务器id */
    filesServerId: string;
    /** 服务id */
    serviceId?: string;
    /** 文件组id */
    filesGroupId?: string;
};

/** 文件属性 */
export interface BaseFilesProps extends
    Pick<BaseWebUniversalProps, 'defaultService' | 'displayOptions' | 'editOptions' | 'sendPageData'>,
    Pick<CommonProps, 'closeModal'> {
    /** 插入文件方法 */
    insertFile?: (payload: InsertFileOptions) => Promise<RtnType>;
    /** 默认选择 */
    defaultSelectedOptions?: DefaultSelectedOptions;
    /** 选择模式：多选或单选 */
    selectMode?: 'multiple' | 'single';
};

/**
 * 文件管理基础组件
 * 支持文件服务器/服务号/文件组三级筛选，图库/表格双视图，
 * 并提供单选或多选插入文件的功能。
 * @param props - 组件属性
 * @param ref - 转发 ref，用于访问内部 BaseWebUniversal 实例
 */
const BaseFiles = forwardRef<any, BaseFilesProps>((props, ref) => {
    const { defaultService, insertFile, selectMode = 'multiple',
        closeModal, sendPageData, defaultSelectedOptions
    } = props;
    const apiId = 'files';
    /** 内部 ref，用于调用 BaseWebUniversal 暴露的方法 */
    const internalRef = useRef<any>(undefined);
    /** 合并外部 ref 和内部 ref */
    const mergedRef = useMergedRef(internalRef, ref);
    /** 文件服务器列表数据 */
    const [filesServerData, setFilesServerData] = useState<any>();
    /** 当前选中的文件服务器 ID */
    const [selectedFilesServerId, setSelectedFilesServerId] = useState<string | undefined>(undefined);
    /** 当前服务器下的服务号列表数据 */
    const [serviceData, setServiceData] = useState<any>();
    /** 当前选中的服务号 ID */
    const [selectedServiceId, setSelectedServiceId] = useState<string | undefined>(undefined);
    /** 当前服务号下的文件组列表数据 */
    const [filesGroupData, setFilesGroupData] = useState<any>();
    /** 当前选中的文件组 ID */
    const [selectedFilesGroupId, setSelectedFilesGroupId] = useState<string | undefined>(undefined);

    /** 多选模式下选中的行数据 */
    const [selectedRows, setSelectedRows] = useState<Array<any>>([]);
    /** 使用 ref 保存最新的选中行，避免闭包陈旧值 */
    const selectedRowsRef = useRef(selectedRows);
    // 始终保持 ref 与 state 同步
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows]);

    /**
     * 获取文件组列表及图片数据
     * @param options - 包含文件服务器ID、服务号ID、文件组ID（可选）
     */
    const getFilesGroupData = async (
        options: {
            /** 文件服务器id */
            filesServerId?: string;
            /** 服务id */
            serviceId?: string;
            /** 文件组id */
            filesGroupId?: string;
        }
    ) => {
        setSelectedRows([]);
        const { filesServerId = selectedFilesServerId,
            serviceId = selectedServiceId,
            filesGroupId = 'allFilesGroup' } = options;
        setSelectedServiceId(serviceId);
        fetchApi.GET(defaultService,
            {
                apiId: 'filesGroup',
                apiExtend: 'showList',
                apiData: {
                    sendPageData: {
                        filesServerId,
                        serviceId
                    }
                }
            }).then(res => {
                if (res.success) {
                    // 在文件组列表前添加“所有”选项
                    setFilesGroupData([{ id: 'allFilesGroup', groupName: '所有' }, ...res.data]);
                    setSelectedFilesGroupId(filesGroupId);
                }
                internalRef?.current?.fetchData?.({
                    apiId,
                    sendPageData: {
                        filesServerId,
                        serviceId,
                        filesGroupId
                    }
                });
            })
    };

    /**
     * 获取服务号数据
     * @param options - 包含文件服务器ID，以及可选的服务号ID和文件组ID
     */
    const getServiceData = async (options: {
        /** 文件服务器id */
        filesServerId: string;
        /** 服务id */
        serviceId?: string;
        /** 文件组id */
        filesGroupId?: string;
    }) => {
        setSelectedRows([]);
        let { filesServerId, serviceId, filesGroupId } = options;
        setSelectedFilesServerId(filesServerId);
        fetchApi.GET(defaultService,
            {
                apiId: 'filesService',
                apiExtend: 'showList',
                apiData: {
                    sendPageData: {
                        filesServerId
                    }
                }
            }).then(res => {
                if (res.success) {
                    setServiceData(res.data);
                    if (!serviceId) {
                        serviceId = selectedServiceId;
                    }
                } else {
                    serviceId = undefined;
                    setServiceData(undefined);
                    setFilesGroupData(undefined);
                    setSelectedFilesGroupId(undefined);
                }
                getFilesGroupData({
                    filesServerId,
                    serviceId,
                    filesGroupId
                });
            })
    };

    // 初始化：获取文件服务器列表，并根据默认选项或默认服务器加载数据
    useEffect(() => {
        fetchApi.GET(defaultService,
            {
                apiId: 'filesServer',
                apiExtend: 'showList'
            }).then(res => {
                if (res.success) {
                    setFilesServerData(res.data);
                    if (defaultSelectedOptions) {
                        if (defaultSelectedOptions?.serviceId) {
                            getServiceData({
                                filesServerId: defaultSelectedOptions?.filesServerId,
                                serviceId: defaultSelectedOptions?.serviceId,
                                filesGroupId: defaultSelectedOptions.filesGroupId
                            })
                        } else {
                            for (const item of res.data) {
                                const { isDefault, defaultServiceId } = item;
                                if (isDefault === true) {
                                    getServiceData({
                                        filesServerId: defaultSelectedOptions?.filesServerId,
                                        serviceId: defaultServiceId,
                                        filesGroupId: defaultSelectedOptions.filesGroupId
                                    })
                                    break;
                                }
                            }
                        }
                    }
                    else {
                        for (const item of res.data) {
                            const { id, isDefault, defaultServiceId } = item;
                            if (isDefault === true) {
                                getServiceData({
                                    filesServerId: id,
                                    serviceId: defaultServiceId
                                });
                                break;
                            }
                        }
                    }
                }
            })
    }, []);

    /**
     * 判断文件类型是否为图片
     * @param fileType - 文件 MIME 类型
     * @returns 如果是图片类型返回 true，否则 false
     */
    const isImage = (fileType: string) => {
        let returnData = false
        if (fileType.slice(0, 5) === 'image') {
            returnData = true
        }
        return returnData;
    };

    /**
     * 获取文件访问地址（用于展示或下载）
     * @param record - 文件记录对象
     * @param isDownload - 是否为下载模式，true 时返回原始文件路径，否则优先使用缩略图
     * @returns 完整的文件 URL
     */
    const getFile = (record: any, isDownload?: boolean) => {
        const { destination, fileName, smallImage, serverUrl } = record;
        const filePath = `${serverUrl}/${destination}/` +
            (isDownload === true ? fileName : (smallImage ? smallImage : fileName));
        return filePath;
    };

    /**
     * 覆盖删除按钮方法，执行物理删除
     * @param options - 包含当前行记录
     * @returns 操作结果
     */
    const onDelete = async (options: DeleteOptions): Promise<RtnType> => {
        let rtn = defaultRtn();
        try {
            const { record } = options;
            //删除文件管理的数据(物理删除)
            rtn = await fetchApi.DELETE(
                defaultService,
                {
                    apiId: 'files',
                    apiExtend: 'deleteFiles',
                    apiData: {
                        filesIds: [{ id: record.id }],
                        sendPageData: {
                            filesServerId: selectedFilesServerId,
                            serviceId: selectedServiceId
                        }
                    }
                })
            if (rtn.success === true) {
                antdMessage('success', '删除成功', { description: 'success' });
                internalRef.current?.fetchData({
                    apiId
                });
            }
            else {
                antdMessage('error', '删除失败', { description: rtn.message });
            }
        }
        catch (err: any) {
            antdMessage('error', '删除失败', { description: `${err.message}，[${err.errors.status}]` });
        }
        return rtn;
    };

    /** 表格列定义 */
    const columnsHead = [
        {
            title: '文件组',
            dataIndex: 'filesGroup',
            render: (text: any) => (text.groupName)
        },
        {
            title: '子目录',
            dataIndex: 'subdirectory',
            render: (text: any) =>
                <Tooltip placement="topLeft" title={text}>{
                    text?.length >= 8 ? text?.substring(0, 8) + '......' : text
                }</Tooltip>
        },
        {
            title: '日期目录',
            dataIndex: 'dateSubdirectory',
        },
        {
            title: '名称',
            dataIndex: 'originalFileName',
            render: (text: any) => <Tooltip placement="topLeft" title={text}>{
                (typeof text !== "undefined" && text !== null) ?
                    (text.length >= 8 ? text.substring(0, 8) + '......' : text)
                    : ''
            }</Tooltip>
        },
        {
            title: '文件',
            dataIndex: 'fileName',
            render: (text: any) => <Tooltip placement="topLeft" title={text}>{
                (typeof text !== "undefined" && text !== null) ?
                    (text.length >= 8 ? text.substring(0, 8) + '......' : text)
                    : ''
            }</Tooltip>
        },
        {
            title: '目录',
            dataIndex: 'destination',
            render: (text: any) => showText(text, { pos: 'suffix' })
        },
        {
            title: '有效期',
            dataIndex: 'fileValidityPeriod',
            render: (val: any, record: any) => <Tooltip placement="topLeft" title={val?.id !== 'permanent' ?
                (typeof record.deletionDate !== 'undefined' ? `到期：${dayjs(record.deletionDate).format('YYYY-MM-DD HH:mm:ss')}` : '')
                : undefined}>
                {val?.name}
            </ Tooltip>
        },
        {
            title: '图片',
            dataIndex: 'id',
            render: (text: any, record: any) => (
                isImage(record.fileType) ?
                    < Image
                        src={getFile(record)}
                        width={60}
                        alt="图片" /> :
                    showText(record.fileType, { pos: 'suffix' })
            )
        },
        {
            title: '下载',
            dataIndex: 'id',
            render: (text: any, record: any) =>
                <a href={getFile(record, true)} >
                    <Tooltip title={'请用鼠标右键点击下载（另存）！'}>下载</Tooltip></a>
        },
        {
            title: '更新',
            dataIndex: '',
            // 预留更新功能，当前未实现
        }
    ];

    /** 图库视图的列定义（用于 gallery 模式） */
    const galleryColumns = useMemo(() => [
        {
            dataIndex: 'id',
            render: (val: string, record: any) => {
                return (
                    <div key={val} style={{ textAlign: 'center' }}>
                        {isImage(record.fileType) ? (
                            <Image
                                src={getFile(record)}
                                height={120}
                                alt="图片"
                            />
                        ) : (
                            <span>{record.fileType}</span>
                        )}
                        <div style={{ marginTop: 8 }}>
                            {/* 多选模式下显示复选框 */}
                            {selectMode === 'multiple' ? <Checkbox
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedRows(prev => [...prev, record]);
                                    } else {
                                        setSelectedRows(prev => prev.filter(row => row.id !== record.id));
                                    }
                                }}
                            > {showText('多选')}
                            </Checkbox> : null}
                            <Button
                                type="primary"
                                style={{ marginLeft: 8 }}
                                onClick={async () => {
                                    // 当前点击的文件
                                    const insertFileRows = [record];
                                    // 获取最新的多选记录（通过 ref）
                                    const currentSelectedRows = selectedRowsRef.current;
                                    // 合并当前选中文件与已选中的多选记录，去重
                                    const merged = [
                                        ...currentSelectedRows,
                                        ...insertFileRows.filter(
                                            newRow => !currentSelectedRows.some(
                                                row => row?.id === newRow?.id
                                            )
                                        )
                                    ];
                                    if (insertFile) {
                                        const rtn = await insertFile({
                                            sendPageData,
                                            fileRows: merged,
                                        });
                                        if (rtn?.success) {
                                            closeModal?.(
                                                {
                                                    success: rtn.success,
                                                    data: {
                                                        sendPageData,
                                                        fileRows: merged,
                                                    }
                                                });
                                        } else {
                                            antdMessage('error', '插入数据错误。', { description: rtn?.message });
                                        }
                                    } else {
                                        closeModal?.({
                                            success: true,
                                            data: {
                                                sendPageData,
                                                fileRows: merged,
                                            }
                                        });
                                    }
                                }}
                            >
                                {showText('选择', {
                                    tooltipText: `${record.originalFileName} - ${record.id} - ${record.fileType} - ${numberHandle.formatNumber(record.fileSize)}(${record.imageHeight}x${record.imageWidth})`,
                                })}
                            </Button>
                        </div>
                    </div>
                );
            },
        },
    ], [selectedRows, isImage, getFile, showText]);

    /** 组合完整的列定义（基础列加上 id 列） */
    const columns = columnsheadId
        .concat(columnsHead as []);

    /** 扩展的 BaseWebUniversal 属性 */
    let extendProps: BaseWebUniversalProps = {
        ...props,
        titleName: '文件',
        apiId,
        columns,
        displayOptions: {
            autoFetch: false,
            viewMode: props?.displayOptions?.viewMode ? props?.displayOptions?.viewMode : 'both',
            showAdd: false,
            galleryColumns
        },
        editOptions: {
            Editor: FilesEdit
        },
        onCallbacks: {
            onAfterAction: () => {
                // 操作成功后清空多选
                setSelectedRows([]);
            },
            onDelete,
        },
        /** 表格头部额外区域渲染：服务器/服务号/文件组选择及上传按钮 */
        renderHeadExtra: ({ rows, fetchData }) => (
            <>
                <Select
                    showSearch
                    placeholder="服务器"
                    optionFilterProp="children"
                    value={selectedFilesServerId}
                    onChange={async (e, option: any) => {
                        const { defaultServiceId } = option?.item;
                        await getServiceData({
                            filesServerId: e,
                            serviceId: defaultServiceId
                        })
                    }}
                    style={{ width: 130 }}
                    filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                    {getList.general(filesServerData, {
                        itemName: 'serverName'
                    })}
                </Select>
                <Select
                    showSearch
                    placeholder="服务号"
                    optionFilterProp="children"
                    value={selectedServiceId}
                    onChange={(e) => {
                        getFilesGroupData({
                            serviceId: e
                        })
                    }}
                    style={{ width: 130 }}
                    filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                    {getList.general(serviceData, {
                        itemName: 'serviceName'
                    })}
                </Select>
                <Select
                    showSearch
                    placeholder="文件组"
                    optionFilterProp="children"
                    value={selectedFilesGroupId}
                    onChange={(e) => {
                        getFilesGroupData({
                            filesGroupId: e
                        })
                    }}
                    style={{ width: 120 }}
                    filterOption={(input, option: any) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                    {getList.general(filesGroupData, {
                        itemName: 'groupName'
                    })}
                </Select>

                <FileUpload
                    uploadOptions={{
                        filesServerId: selectedFilesServerId,
                        serviceId: selectedServiceId!,
                        filesGroupId: selectedFilesGroupId!,
                        defaultService
                    }}
                    // 当文件组为“所有”或未选择时禁止上传
                    disabled={selectedFilesGroupId === 'allFilesGroup' || selectedFilesGroupId === undefined}
                    onUploadSuccess={(file) => {
                        antdMessage('success', '文件上传成功', { description: 'success' });
                        fetchData?.();
                    }}
                    buttonText="上传文件"
                />
            </>
        )
    };

    return (
        <>
            <BaseWebUniversal {...extendProps} ref={mergedRef} />
        </>
    )
});

export default BaseFiles;